import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CREDIT_API_URL } from '../Constants';

/**
 * Reusable autocomplete hook for search APIs
 * @param {Object} config - Configuration object
 * @param {string} config.apiEndpoint - API endpoint (e.g., '/pincode/search' or '/company/search')
 * @param {number} config.minChars - Minimum characters required to trigger search (default: 2)
 * @param {number} config.debounceDelay - Debounce delay in ms (default: 400)
 * @param {Function} config.onSelect - Callback when an item is selected
 * @param {Function} config.getValue - Function to extract display value from suggestion item
 * @param {Function} config.getKey - Function to extract key from suggestion item (for React key)
 * @returns {Object} - Autocomplete state and handlers
 */
const useAutocomplete = ({
  apiEndpoint,
  minChars = 2,
  debounceDelay = 400,
  onSelect,
  getValue = (item) => item, // Default: return item as string
  getKey = (item, index) => index // Default: use index as key
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search API call
  const searchAPI = useCallback(async (query) => {
    if (!query || query.length < minChars) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${CREDIT_API_URL}${apiEndpoint}`, {
        params: { query }
      });

      if (response.data && response.data.success && response.data.suggestions) {
        setSuggestions(response.data.suggestions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error(`Error fetching autocomplete suggestions from ${apiEndpoint}:`, error);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, [apiEndpoint, minChars]);

  // Debounced search
  const debouncedSearch = useCallback((query) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchAPI(query);
    }, debounceDelay);
  }, [searchAPI, debounceDelay]);

  // Handle item selection
  const handleSelect = useCallback((item) => {
    if (onSelect) {
      onSelect(item);
    }
    setShowDropdown(false);
    setSuggestions([]);
  }, [onSelect]);

  return {
    suggestions,
    showDropdown,
    isLoading,
    inputRef,
    dropdownRef,
    debouncedSearch,
    handleSelect,
    getValue,
    getKey,
    setShowDropdown
  };
};

export default useAutocomplete;

